import { Menu, MenuItem, Snackbar } from '@material-ui/core';
import React, { useState } from 'react';
import { unstable_batchedUpdates } from 'react-dom';
import { track } from '~/src/dash/analytics/analytics';

import { uploadBulkuploaderCSVfile } from '~/src/dash/api/console-api';
import { IconV2 } from '~/src/dash/components/icons/IconV2';
import { ModelSnackBarProgressBarContainer } from '~/src/dash/components/model-snackbar-progresbar-container';
import { ButtonV2, Col, Cols, spacing, Text, usePublicaTranslation } from '~/src/dash/publicaUI';
import { getBulkUploaderDocumentationWiki } from '~/src/dash/resellers/utils/importResellerConfig';
import { genDownloadCampaignsCSVURL } from '~/src/dash/router/routes';
import { publicaColors } from '~/src/dash/utils/colors';
import UploadManager from '~/src/dash/utils/upload_manager';
import { useBaseStore } from '~/src/dash/views/base/contexts';
import { SnackbarView } from '~/src/dash/views/snackbar-view';

interface IProps {
  isDisabled?: boolean;
  handleSuccess: () => void;
}

export const BulkUploadMenu = ({ isDisabled, handleSuccess }: IProps): React.ReactNode => {
  const { session } = useBaseStore();
  const { t } = usePublicaTranslation();
  const { user, isSuperUser } = useBaseStore();

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarError, setSnackbarError] = useState(false);

  const uploadManager = new UploadManager();

  const uploadCSV = (isNewCSV: boolean, { target }: React.ChangeEvent<HTMLInputElement>): void => {
    if (target.files && target.files.length > 0) {
      const file = target.files[0];
      const filenameParams = file.name.split('.');
      const ext = filenameParams[filenameParams.length - 1];
      if (ext.toLowerCase() !== 'csv') {
        unstable_batchedUpdates(() => {
          setSnackbarMessage(t('ErrorOnlyCSVFilesAreSupported'));
          setSnackbarError(true);
        });
        return;
      }
      target.value = '';
      const uploadKey = uploadManager.addUpload(file.name);

      uploadBulkuploaderCSVfile({
        publisher_id: session.publisher.ID,
        name: filenameParams[0],
        order_id: '',
        template_id: isNewCSV ? 'campaign_new' : 'campaign_updates',
        file,
        managerFunc: (xhr: XMLHttpRequest) => {
          uploadManager.addListenersToUpload(uploadKey, xhr);
        },
      })
        .then((json: { response: { success: boolean; errors?: string[] } }) => {
          if (json.response.success) {
            try {
              uploadManager.signalSuccess(uploadKey);
            } catch (e) {
              uploadManager.signalFailure(uploadKey);
            }
          } else {
            uploadManager.signalFailure(uploadKey);
          }
          if (json.response.errors && json.response.errors.length > 0) {
            unstable_batchedUpdates(() => {
              setSnackbarMessage(json.response.errors as unknown as string);
              setSnackbarError(true);
            });
          } else {
            unstable_batchedUpdates(() => {
              setSnackbarMessage(t('SnackbarSuccessfullyUpdatedItem', { item: t('LineItems') }));
              setSnackbarError(false);
            });
            handleSuccess();
          }
        })
        .catch(() => {
          uploadManager.signalFailure(uploadKey);
          unstable_batchedUpdates(() => {
            setSnackbarMessage(t('SnackbarFailedToUpdateItem', { item: t('LineItems') }));
          });
        });
    }
  };

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>): void => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = (): void => {
    setAnchorEl(null);
  };

  return (
    <div style={{ zIndex: 9999 }}>
      <ButtonV2 onClick={handleClick} disabled={isDisabled}>
        <Cols
          gap={spacing.small}
          style={{
            alignItems: 'center',
          }}
        >
          <Col displayFlex>
            <IconV2 name='upload' color={isDisabled ? publicaColors.gray : publicaColors.white} />
          </Col>
          <Col>
            <Text inheritColor inheritWeight tKey='BulkUpload' />
          </Col>
        </Cols>
      </ButtonV2>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: -4, horizontal: 'right' }}
      >
        {/* CREATE NEW LINES */}
        <MenuItem
          onClick={() => {
            window.location.href = genDownloadCampaignsCSVURL(
              session.publisher.ID,
              true,
              '',
              session.publisher.Name,
            );
            track(
              'Line Item List - Bulk Upload',
              { isSuperUser, upload: 'Create New Lines' },
              user.user,
            );
            handleClose();
          }}
        >
          <Cols gap={spacing.small} style={{ alignItems: 'center' }}>
            <Col displayFlex>
              <IconV2 name='download' />
            </Col>
            <Col>
              <Text tKey='CreateNewItem' tProps={{ item: t('Lines') }} />
            </Col>
          </Cols>
        </MenuItem>
        {/* CREATE LINE UPDATES */}
        <MenuItem
          onClick={() => {
            window.location.href = genDownloadCampaignsCSVURL(
              session.publisher.ID,
              false,
              '',
              session.publisher.Name,
            );
            track(
              'Line Item List - Bulk Upload',
              { isSuperUser, upload: 'Create Line Updates' },
              user.user,
            );
            handleClose();
          }}
        >
          <Cols gap={spacing.small} style={{ alignItems: 'center' }}>
            <Col displayFlex>
              <IconV2 name='download' />
            </Col>
            <Col>
              <Text tKey='CreateItem' tProps={{ item: t('LineUpdates') }} />
            </Col>
          </Cols>
        </MenuItem>
        {/* UPLOAD NEW LINES */}
        <MenuItem
          onClick={() => {
            track(
              'Line Item List - Bulk Upload',
              { isSuperUser, upload: 'Upload New Lines' },
              user.user,
            );
          }}
        >
          <Cols gap={spacing.small} style={{ alignItems: 'center' }}>
            <Col displayFlex>
              <IconV2 name='upload' />
            </Col>
            <Col>
              <label style={{ cursor: 'pointer', width: '100%' }}>
                <Text tKey='UploadNewItem' tProps={{ item: t('Lines') }} />
                <input
                  type='file'
                  style={{ display: 'none' }}
                  accept='.csv'
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                    uploadCSV(true, event);
                    handleClose();
                  }}
                />
              </label>
            </Col>
          </Cols>
        </MenuItem>
        {/* UPLOAD LINE UPDATES */}
        <MenuItem
          onClick={() => {
            track(
              'Line Item List - Bulk Upload',
              { isSuperUser, upload: 'Upload Line Updates' },
              user.user,
            );
          }}
        >
          <Cols gap={spacing.small} style={{ alignItems: 'center' }}>
            <Col displayFlex>
              <IconV2 name='upload' />
            </Col>
            <Col>
              <label style={{ cursor: 'pointer', width: '100%' }}>
                <Text tKey='UploadItem' tProps={{ item: t('LineUpdates') }} />
                <input
                  type='file'
                  style={{ display: 'none' }}
                  accept='.csv'
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                    uploadCSV(false, event);
                    handleClose();
                  }}
                />
              </label>
            </Col>
          </Cols>
        </MenuItem>
        {/* BULK UPLOADER WIKI */}
        <MenuItem
          onClick={() => {
            window.open(getBulkUploaderDocumentationWiki(), '_blank');
            track(
              'Line Item List - Bulk Upload',
              { isSuperUser, upload: 'Bulk Uploader Wiki' },
              user.user,
            );
            handleClose();
          }}
        >
          <Cols gap={spacing.small} style={{ alignItems: 'center' }}>
            <Col displayFlex>
              <IconV2 name='externalLink' />
            </Col>
            <Col>
              <Text tKey='BulkUploaderWiki' />
            </Col>
          </Cols>
        </MenuItem>
      </Menu>
      {/* snackbar for upload progress */}
      <Snackbar
        open={true}
        anchorOrigin={{
          horizontal: 'right',
          vertical: 'bottom',
        }}
      >
        <ModelSnackBarProgressBarContainer uploadManager={uploadManager} />
      </Snackbar>
      <SnackbarView
        open={!!snackbarMessage}
        error={!!snackbarError}
        onClose={() => setSnackbarMessage('')}
      >
        {snackbarMessage}
      </SnackbarView>
    </div>
  );
};
